import { c as _c } from "react/compiler-runtime";
import { MantineThemeColors, useSafeMantineTheme } from '@mantine/core';
import { ScaleLinear, scaleLinear } from 'd3-scale';
import { CSSProperties, FC } from 'react';
import { z } from 'zod';
import { zx } from '../../type';
import { geomOfWholePct } from 'shared/agg';
import { notNull } from 'shared/type';
import { Fmt } from 'shared/forecast/Fmt';
import { GSpace } from '../svg/Svg';
import { extent } from 'd3-array';
import { XMetricLabelProps } from '../Labels';
import { ForecastBinBar, binForecast, binForecastScale } from '../ForecastBar';
import { ChartCfg, ChartMeta } from './chartMeta.types';
import { ChartComponentProps, useDebouncedSize } from './charts';
const zForecastRow = {
  forecast: z.object({
    likelihood: z.number().describe('Likelihood'),
    comments: zx.nullish.string.describe('Comments')
  })
};
export type ForecastRow = z.infer<(typeof zForecastRow)['forecast']>;
export type ForecastBarCfg = ChartCfg<{
  forecast: ForecastRow[];
}> & {
  labels: {
    before?: string;
    after?: string;
  };
  color?: string;
};
export type ForecastBarProps = ChartComponentProps<{
  forecasts: ForecastBarCfg['sheets']['forecast'];
} & Pick<ForecastBarCfg, 'labels' | 'color'>>;

/**
 * Legacy viz
 */
export const ForecastBar: FC<ForecastBarProps> = ({
  chart: {
    title,
    forecasts,
    color
  }
}) => {
  const {
    colors
  } = useSafeMantineTheme();
  const dim = useDebouncedSize();
  const width = dim.width == 0 ? 400 : dim.width; // default width for SSR
  const data = layout({
    forecasts,
    width: width - 30,
    color,
    colors
  });
  return <div className='flex w-full flex-col items-center gap-2'>
      <h2>{title}</h2>
      <div ref={dim.ref} className='w-full max-w-[60rem]'>
        {forecasts?.length > 0 && <div className='w-full pl-[10px] pt-[5px]'>
            <ForecastBinBar {...data} showScale format={Fmt.optionsLikelihood} />
          </div>}
      </div>
    </div>;
};
const layout = ({
  forecasts = [],
  width,
  color,
  colors
}: Pick<ForecastBarProps['chart'], 'forecasts' | 'color'> & {
  width: number;
  colors: MantineThemeColors;
}) => {
  const values = forecasts.map(f => f.likelihood).filter(notNull) ?? [];
  const value = geomOfWholePct(values) ?? 0;
  const scale = scaleLinear(extent(values) as number[], [0, width]).nice();
  const bins = binForecast({
    domain: scale,
    forecasts: forecasts.filter(f => f.likelihood != null).map(({
      likelihood: value,
      comments
    }) => ({
      value,
      comments: comments?.split('|')
    })),
    bins: 20
  }).bins;
  const res = {
    value,
    scale,
    forecasts,
    bins,
    scaleBinColor: binForecastScale({
      bins,
      color,
      colors
    })
  };
  return res;
};
type DeltaPart = Pick<XMetricLabelProps, 'value' | 'label' | 'color'>;
type ScaleProps = {
  scale: ScaleLinear<number, number>;
};
export type ForecastDeltaProps = {
  from: DeltaPart;
  to: DeltaPart;
  deltaLabel?: string;
  size?: 'md' | 'lg';
  format?: string;
  style?: CSSProperties;
} & ScaleProps;
export const ForecastDelta = t0 => {
  const $ = _c(49);
  const {
    from,
    to,
    scale,
    deltaLabel,
    size,
    format,
    style
  } = t0;
  const {
    colors
  } = useSafeMantineTheme();
  const beforeX = scale(from.value);
  const afterX = scale(to.value);
  const delta = to.value - from.value;
  const deltaWidth = scale(delta);
  const deltaCenterX = beforeX + deltaWidth / 2;
  const yCenter = size == "lg" ? 12 : 10;
  const textBgWidth = size == "lg" ? 105 : 60;
  let t1;
  if ($[0] !== yCenter) {
    t1 = {
      cy: yCenter,
      r: 6
    };
    $[0] = yCenter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const lineCapProps = t1;
  const labelOutside = deltaLabel || deltaWidth < textBgWidth + 10;
  let t2;
  if ($[2] !== afterX || $[3] !== beforeX || $[4] !== colors.gray[3] || $[5] !== yCenter) {
    t2 = <line x1={beforeX} x2={afterX} y1={yCenter} y2={yCenter} stroke={colors.gray[3]} strokeWidth={6} />;
    $[2] = afterX;
    $[3] = beforeX;
    $[4] = colors.gray[3];
    $[5] = yCenter;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== beforeX || $[8] !== from.color || $[9] !== lineCapProps) {
    t3 = <circle {...lineCapProps} cx={beforeX} fill={from.color} />;
    $[7] = beforeX;
    $[8] = from.color;
    $[9] = lineCapProps;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== afterX || $[12] !== lineCapProps || $[13] !== to.color) {
    t4 = <circle {...lineCapProps} cx={afterX} fill={to.color} />;
    $[11] = afterX;
    $[12] = lineCapProps;
    $[13] = to.color;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== deltaCenterX || $[16] !== labelOutside || $[17] !== textBgWidth || $[18] !== yCenter) {
    t5 = !labelOutside && <rect x={deltaCenterX - textBgWidth / 2} y={0} width={textBgWidth} height={yCenter * 2} className="fill-white" />;
    $[15] = deltaCenterX;
    $[16] = labelOutside;
    $[17] = textBgWidth;
    $[18] = yCenter;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = labelOutside ? afterX + 8 * (delta > 0 ? 1 : -1) : deltaCenterX;
  const t7 = yCenter + 1;
  const t8 = labelOutside ? delta > 0 ? "start" : "end" : "middle";
  let t9;
  if ($[20] !== colors.gray[6] || $[21] !== style || $[22] !== t8) {
    t9 = {
      dominantBaseline: "middle",
      alignmentBaseline: "central",
      textAnchor: t8,
      fill: colors.gray[6],
      ...style
    };
    $[20] = colors.gray[6];
    $[21] = style;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== deltaLabel || $[25] !== to.color) {
    t10 = deltaLabel && <tspan style={{
      fill: to.color
    }}>{`${deltaLabel}: `}</tspan>;
    $[24] = deltaLabel;
    $[25] = to.color;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = `${size == "lg" ? "text-[2em] font-extrabold" : "text-[1.2em] font-bold"}`;
  let t12;
  if ($[27] !== to.color) {
    t12 = {
      fill: to.color
    };
    $[27] = to.color;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== delta || $[30] !== format) {
    t13 = Fmt.fmt(delta, {
      fmt: format,
      showSign: true
    });
    $[29] = delta;
    $[30] = format;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <tspan className={t11} style={t12}>{t13}</tspan>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t10 || $[37] !== t14 || $[38] !== t6 || $[39] !== t7 || $[40] !== t9) {
    t15 = <text x={t6} y={t7} className="bg-white" style={t9}>{t10}{t14}</text>;
    $[36] = t10;
    $[37] = t14;
    $[38] = t6;
    $[39] = t7;
    $[40] = t9;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <GSpace height={24} />;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4 || $[47] !== t5) {
    t17 = <>{t2}{t3}{t4}{t5}{t15}{t16}</>;
    $[43] = t15;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
};
const type = 'ForecastBar';
export const forecastBarMeta: ChartMeta<ForecastBarProps['chart'], ForecastBarCfg> = {
  type,
  title: 'Forecast bars',
  View: ForecastBar,
  edit: {
    initial: () => ({
      source: '',
      labels: {}
    }),
    parser: zForecastRow,
    cfgToProps: ({
      sheets: {
        forecast
      },
      ...cfg
    }) => ({
      forecasts: forecast ?? [],
      ...cfg
    }),
    cfgSchema: {
      color: {
        label: 'Color',
        type: 'color',
        group: 'color'
      }
    }
  }
};